import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Save,
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Code,
  Quote,
  Minus,
  Paperclip,
  Trash2,
  Lock,
  Unlock,
  Eye,
  Edit3,
  Columns,
  Sparkles,
  Shield,
  FileText,
  Clock,
  Check,
  AlertCircle,
  Share2,
  Download,
  Plus,
} from 'lucide-react';

export default function SecureNoteEditorModal({
  open,
  note, // null if new note, or existing note object
  categories = [],
  onClose,
  onSave,
  hasEditPermission = true,
  onOpenTemplates,
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Personal');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockPin, setLockPin] = useState('');
  const [showPinInput, setShowPinInput] = useState(false);
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'attachments' | 'sharing'
  const [viewMode, setViewMode] = useState('split'); // 'edit' | 'preview' | 'split'
  const [autosaveStatus, setAutosaveStatus] = useState('saved'); // 'saved' | 'saving' | 'dirty'
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Sharing permissions in editor
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('VIEW'); // 'VIEW' | 'EDIT'
  const [sharesList, setSharesList] = useState([]);

  const textareaRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const initialLoadRef = useRef(false);

  // Lock body scroll on mobile when editor is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  // Initialize or reset state when modal opens
  useEffect(() => {
    if (!open) {
      initialLoadRef.current = false;
      return;
    }

    if (note) {
      setTitle(note.title || '');
      setCategory(note.category || 'Personal');
      setTags(Array.isArray(note.tags) ? note.tags : []);
      setContent(note.content || '');
      setAttachments(Array.isArray(note.attachments) ? note.attachments : []);
      setIsLocked(Boolean(note.isLocked));
      setLockPin(note.lockPin || '');
      setShowPinInput(Boolean(note.isLocked));
      setSharesList(Array.isArray(note.shares) ? note.shares : []);
      setAutosaveStatus('saved');
      setLastSavedTime(note.updated_at ? new Date(note.updated_at) : new Date());
    } else {
      setTitle('');
      setCategory(categories[0]?.name || 'Personal');
      setTags([]);
      setContent('');
      setAttachments([]);
      setIsLocked(false);
      setLockPin('');
      setShowPinInput(false);
      setSharesList([]);
      setAutosaveStatus('saved');
      setLastSavedTime(null);
    }
    setError('');
    setActiveTab('editor');
    initialLoadRef.current = true;
  }, [open, note, categories]);

  // Autosave simulation/debounce
  const triggerAutosave = useCallback(() => {
    if (!initialLoadRef.current || !note?.id) return;
    setAutosaveStatus('dirty');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = setTimeout(async () => {
      if (!hasEditPermission) return;
      setAutosaveStatus('saving');
      try {
        // Save draft to localStorage as immediate offline safeguard
        const draftKey = `ciphervault_note_draft_${note.id}`;
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            title,
            category,
            tags,
            content,
            attachments,
            isLocked,
            lockPin,
            savedAt: new Date().toISOString(),
          })
        );
        setAutosaveStatus('saved');
        setLastSavedTime(new Date());
      } catch (e) {
        setAutosaveStatus('dirty');
      }
    }, 2000);
  }, [note, title, category, tags, content, attachments, isLocked, lockPin, hasEditPermission]);

  useEffect(() => {
    if (initialLoadRef.current) {
      triggerAutosave();
    }
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [title, content, category, tags, attachments, isLocked, lockPin, triggerAutosave]);

  if (!open) return null;

  // Insert markdown formatting into textarea
  const insertFormatting = (prefix, suffix = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentVal = textarea.value;
    const selectedText = currentVal.substring(start, end) || placeholder;

    const replacement = `${prefix}${selectedText}${suffix}`;
    const newContent = currentVal.substring(0, start) + replacement + currentVal.substring(end);

    setContent(newContent);
    triggerAutosave();

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + selectedText.length
      );
    }, 10);
  };

  // Tag management
  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '').toLowerCase();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // Attachment upload (encrypts client side as base64 in the payload)
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} exceeds 10MB limit`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const newAttachment = {
          id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          data: reader.result, // base64 Data URL (encrypted end-to-end)
          uploadedAt: new Date().toISOString(),
        };
        setAttachments((prev) => [...prev, newAttachment]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemoveAttachment = (attId) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attId));
  };

  // Sharing
  const handleAddShare = (e) => {
    e.preventDefault();
    if (!shareRecipientEmail.trim()) return;
    const newShare = {
      id: `share-${Date.now()}`,
      email: shareRecipientEmail.trim().toLowerCase(),
      permission: sharePermission,
      createdAt: new Date().toISOString(),
    };
    setSharesList([...sharesList, newShare]);
    setShareRecipientEmail('');
  };

  const handleRemoveShare = (shareId) => {
    setSharesList(sharesList.filter((s) => s.id !== shareId));
  };

  // Interactive checklist toggle inside markdown preview
  const handleChecklistToggle = (lineIndex) => {
    const lines = content.split('\n');
    const targetLine = lines[lineIndex];
    if (targetLine.includes('- [ ] ')) {
      lines[lineIndex] = targetLine.replace('- [ ] ', '- [x] ');
    } else if (targetLine.includes('- [x] ')) {
      lines[lineIndex] = targetLine.replace('- [x] ', '- [ ] ');
    }
    setContent(lines.join('\n'));
    triggerAutosave();
  };

  // Render markdown preview
  const renderMarkdownPreview = () => {
    if (!content.trim()) {
      return (
        <div className="note-preview-empty">
          <FileText size={32} opacity={0.3} />
          <p>Note is empty. Use the toolbar or start typing to format your secure note.</p>
        </div>
      );
    }

    const lines = content.split('\n');
    let inCodeBlock = false;
    let codeBuffer = [];

    return (
      <div className="note-markdown-rendered">
        {lines.map((line, idx) => {
          // Code block toggles
          if (line.trim().startsWith('```')) {
            if (inCodeBlock) {
              inCodeBlock = false;
              const codeText = codeBuffer.join('\n');
              codeBuffer = [];
              return (
                <div key={idx} className="note-code-block-wrapper">
                  <pre className="note-code-block">
                    <code>{codeText}</code>
                  </pre>
                </div>
              );
            } else {
              inCodeBlock = true;
              return null;
            }
          }
          if (inCodeBlock) {
            codeBuffer.push(line);
            return null;
          }

          // Headings
          if (line.startsWith('# ')) {
            return <h1 key={idx} className="note-h1">{line.replace('# ', '')}</h1>;
          }
          if (line.startsWith('## ')) {
            return <h2 key={idx} className="note-h2">{line.replace('## ', '')}</h2>;
          }
          if (line.startsWith('### ')) {
            return <h3 key={idx} className="note-h3">{line.replace('### ', '')}</h3>;
          }

          // Horizontal divider
          if (line.trim() === '---' || line.trim() === '***') {
            return <hr key={idx} className="note-divider" />;
          }

          // Blockquote
          if (line.startsWith('> ')) {
            return (
              <blockquote key={idx} className="note-blockquote">
                {line.replace('> ', '')}
              </blockquote>
            );
          }

          // Checklists: interactive check toggle!
          if (line.trim().startsWith('- [ ] ') || line.trim().startsWith('- [x] ')) {
            const checked = line.trim().startsWith('- [x] ');
            const labelText = line.trim().replace(checked ? '- [x] ' : '- [ ] ', '');
            return (
              <div
                key={idx}
                className={`note-checklist-item ${checked ? 'completed' : ''}`}
                onClick={() => handleChecklistToggle(idx)}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleChecklistToggle(idx)}
                  className="note-checklist-checkbox"
                />
                <span className="note-checklist-label">{labelText}</span>
              </div>
            );
          }

          // Bullet item
          if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
            return (
              <li key={idx} className="note-bullet-item">
                {line.trim().substring(2)}
              </li>
            );
          }

          // Numbered item
          const numMatch = line.trim().match(/^(\d+)\.\s+(.*)/);
          if (numMatch) {
            return (
              <div key={idx} className="note-ordered-item">
                <span className="note-ordered-num">{numMatch[1]}.</span>
                <span>{numMatch[2]}</span>
              </div>
            );
          }

          // Empty line
          if (!line.trim()) {
            return <div key={idx} className="note-paragraph-gap" />;
          }

          // Regular paragraph (parse simple bold & code)
          return (
            <p key={idx} className="note-paragraph">
              {formatInlineText(line)}
            </p>
          );
        })}
      </div>
    );
  };

  const formatInlineText = (text) => {
    // Simple inline parser for bold **text** and inline `code`
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={i} className="note-inline-code">{part.slice(1, -1)}</code>;
      }
      return part;
    });
  };

  // Submit and Save
  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Please provide a title for the secure note');
      return;
    }
    if (isLocked && !lockPin.trim()) {
      setError('Please provide a PIN or password for the note lock');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const now = new Date().toISOString();

      // Create version snapshot
      const existingVersions = Array.isArray(note?.versions) ? note.versions : [];
      const newVersionNumber = existingVersions.length + 1;
      const versionSnapshot = {
        version: newVersionNumber,
        title: title.trim(),
        content,
        savedAt: now,
        summary: note ? `Revision ${newVersionNumber}` : 'Initial version',
      };

      // Create audit log event
      const existingAudit = Array.isArray(note?.auditLog) ? note.auditLog : [];
      const auditEvent = {
        action: note ? 'Updated' : 'Created',
        actor: 'Current Device',
        timestamp: now,
        details: note
          ? `Note updated (v${newVersionNumber}) and re-encrypted with AES-256-GCM`
          : 'Zero-knowledge encrypted note initialized',
      };

      const notePayload = {
        title: title.trim(),
        category,
        tags,
        content,
        attachments,
        isLocked,
        lockPin: isLocked ? lockPin.trim() : null,
        shares: sharesList,
        versions: [...existingVersions, versionSnapshot],
        auditLog: [auditEvent, ...existingAudit],
      };

      await onSave(notePayload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save encrypted note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel secure-note-editor-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={note ? 'Edit Secure Note' : 'New Secure Note'}
      >
        {/* Header */}
        <div className="modal-header secure-note-editor-header">
          {/* Row 1: Single row for Title & Close Button */}
          <div className="editor-header-top-row">
            <div className="editor-header-left">
              <div className="modal-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                <Shield size={16} />
              </div>
              <div className="editor-title-row">
                <h2 className="modal-title">
                  {note ? 'Edit Secure Note' : 'New Secure Note'}
                </h2>
                <span className="zero-knowledge-badge">
                  <Shield size={11} />
                  <span>AES-256</span>
                </span>
              </div>
            </div>

            <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {/* Row 2: Status on left, Templates button & View toggle on right */}
          <div className="editor-header-sub-row">
            <div className="editor-autosave-row">
              {autosaveStatus === 'saving' && (
                <span className="autosave-indicator saving">
                  <Clock size={12} className="spin" />
                  <span>Autosaving...</span>
                </span>
              )}
              {autosaveStatus === 'saved' && (
                <span className="autosave-indicator saved">
                  <Check size={12} />
                  <span>
                    {lastSavedTime ? `Saved ${lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Encrypted'}
                  </span>
                </span>
              )}
              {autosaveStatus === 'dirty' && (
                <span className="autosave-indicator unsaved">
                  <span>Unsaved edits</span>
                </span>
              )}
            </div>

            <div className="editor-header-actions">
              {!note && onOpenTemplates && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm editor-template-btn"
                  onClick={() => {
                    onClose();
                    onOpenTemplates();
                  }}
                  title="Browse Templates"
                >
                  <Sparkles size={13} color="#f59e0b" />
                  <span>Templates</span>
                </button>
              )}

              {/* View Mode Toggle */}
              <div className="editor-view-mode-toggle">
                <button
                  type="button"
                  className={`view-mode-btn ${viewMode === 'edit' ? 'active' : ''}`}
                  onClick={() => setViewMode('edit')}
                  title="Edit Only"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  className={`view-mode-btn view-mode-split ${viewMode === 'split' ? 'active' : ''}`}
                  onClick={() => setViewMode('split')}
                  title="Split View (Editor + Live Preview)"
                >
                  <Columns size={13} />
                </button>
                <button
                  type="button"
                  className={`view-mode-btn ${viewMode === 'preview' ? 'active' : ''}`}
                  onClick={() => setViewMode('preview')}
                  title="Preview Only"
                >
                  <Eye size={13} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert-banner alert-error" style={{ margin: '8px 24px 0 24px' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Tab Navigation (Editor, Attachments, Sharing) */}
        <div className="secure-note-editor-tabs">
          <button
            type="button"
            className={`editor-tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
            onClick={() => setActiveTab('editor')}
          >
            <FileText size={15} />
            <span className="tab-label-full">Note Editor</span>
            <span className="tab-label-short">Editor</span>
          </button>
          <button
            type="button"
            className={`editor-tab-btn ${activeTab === 'attachments' ? 'active' : ''}`}
            onClick={() => setActiveTab('attachments')}
          >
            <Paperclip size={15} />
            <span className="tab-label-full">Attachments ({attachments.length})</span>
            <span className="tab-label-short">Files ({attachments.length})</span>
          </button>
          <button
            type="button"
            className={`editor-tab-btn ${activeTab === 'sharing' ? 'active' : ''}`}
            onClick={() => setActiveTab('sharing')}
          >
            <Share2 size={15} />
            <span className="tab-label-full">Sharing & Permissions ({sharesList.length})</span>
            <span className="tab-label-short">Sharing ({sharesList.length})</span>
          </button>
        </div>

        <form onSubmit={handleSave} className="secure-note-editor-form">
          {activeTab === 'editor' && (
            <div className="editor-main-area">
              {/* Note Metadata Row: Title, Category, Lock Toggle */}
              <div className="editor-meta-row">
                <div className="editor-title-field">
                  <input
                    type="text"
                    className="input-field note-title-input"
                    placeholder="Note Title..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={140}
                    required
                    autoFocus
                  />
                </div>

                <div className="editor-category-field">
                  <select
                    className="input-field select-field"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categories.map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Note Lock Toggle */}
                <div className="editor-lock-field">
                  <button
                    type="button"
                    className={`btn btn-sm ${isLocked ? 'btn-danger' : 'btn-secondary'}`}
                    onClick={() => {
                      const nextLocked = !isLocked;
                      setIsLocked(nextLocked);
                      setShowPinInput(nextLocked);
                    }}
                    title={isLocked ? 'Note is locked with PIN' : 'Lock note with secret PIN'}
                  >
                    {isLocked ? <Lock size={14} /> : <Unlock size={14} />}
                    <span>{isLocked ? 'Locked' : 'Lock Note'}</span>
                  </button>
                </div>
              </div>

              {/* PIN input row if locked */}
              {showPinInput && (
                <div className="editor-pin-row animate-fade-in">
                  <Lock size={15} color="#ef4444" />
                  <span>Set 4-8 digit lock PIN for this confidential note:</span>
                  <input
                    type="password"
                    className="input-field pin-input"
                    placeholder="PIN or Password"
                    value={lockPin}
                    onChange={(e) => setLockPin(e.target.value)}
                    maxLength={16}
                  />
                </div>
              )}

              {/* Tag Chips Row */}
              <div className="editor-tags-row">
                <div className="editor-tags-list">
                  {tags.map((tg) => (
                    <span key={tg} className="editor-tag-chip">
                      #{tg}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tg)}
                        className="editor-tag-remove"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="editor-tag-input"
                    placeholder="Add tags (press Enter)..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                  />
                </div>
              </div>

              {/* Formatting Toolbar */}
              <div className="editor-toolbar">
                <div className="toolbar-group">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('# ', '', 'Heading 1')}
                    title="Heading 1"
                  >
                    <Heading1 size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('## ', '', 'Heading 2')}
                    title="Heading 2"
                  >
                    <Heading2 size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('### ', '', 'Heading 3')}
                    title="Heading 3"
                  >
                    <Heading3 size={16} />
                  </button>
                </div>

                <div className="toolbar-divider" />

                <div className="toolbar-group">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('**', '**', 'bold text')}
                    title="Bold"
                  >
                    <Bold size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('*', '*', 'italic text')}
                    title="Italic"
                  >
                    <Italic size={16} />
                  </button>
                </div>

                <div className="toolbar-divider" />

                <div className="toolbar-group">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('- ', '', 'List item')}
                    title="Bullet List"
                  >
                    <List size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('1. ', '', 'Numbered item')}
                    title="Numbered List"
                  >
                    <ListOrdered size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('- [ ] ', '', 'Checklist item')}
                    title="Checklist"
                  >
                    <CheckSquare size={16} />
                  </button>
                </div>

                <div className="toolbar-divider" />

                <div className="toolbar-group">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('```\n', '\n```', 'code block')}
                    title="Code Block"
                  >
                    <Code size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('> ', '', 'Quote block')}
                    title="Blockquote"
                  >
                    <Quote size={16} />
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => insertFormatting('\n---\n', '', '')}
                    title="Horizontal Line"
                  >
                    <Minus size={16} />
                  </button>
                </div>

                <div className="toolbar-divider" />

                <div className="toolbar-group">
                  <label className="toolbar-btn toolbar-file-label" title="Attach encrypted files">
                    <Paperclip size={16} />
                    <input
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>
              </div>

              {/* Editor & Preview Split/Single Container */}
              <div className={`editor-content-container view-${viewMode}`}>
                {(viewMode === 'edit' || viewMode === 'split') && (
                  <div className="editor-textarea-pane">
                    <textarea
                      ref={textareaRef}
                      className="note-textarea"
                      placeholder="Type your encrypted note in rich markdown... Use # for headings, - [ ] for checklists, ``` for code blocks."
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                    />
                  </div>
                )}

                {(viewMode === 'preview' || viewMode === 'split') && (
                  <div className="editor-preview-pane">
                    <div className="preview-pane-header">
                      <Eye size={13} />
                      <span>Encrypted Live Preview</span>
                    </div>
                    {renderMarkdownPreview()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="editor-attachments-tab">
              <div className="attachments-top-bar">
                <div>
                  <h3 className="section-title">Encrypted Attachments</h3>
                  <p className="section-subtitle">
                    Files are encrypted on your device using AES-256-GCM before syncing. Plaintext files never reach CipherVault servers.
                  </p>
                </div>
                <label className="btn btn-primary btn-sm file-upload-btn">
                  <Paperclip size={14} />
                  <span>Upload File</span>
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                </label>
              </div>

              {attachments.length === 0 ? (
                <div className="attachments-empty-state">
                  <Paperclip size={40} opacity={0.3} />
                  <p>No attachments yet. Add images, documents, PGP keys, or runbooks to this secure note.</p>
                </div>
              ) : (
                <div className="attachments-grid">
                  {attachments.map((att) => {
                    const sizeKb = (att.size / 1024).toFixed(1);
                    return (
                      <div key={att.id} className="attachment-card">
                        <div className="attachment-icon-box">
                          <FileText size={20} color="#3b82f6" />
                        </div>
                        <div className="attachment-info">
                          <strong className="attachment-name text-truncate" title={att.name}>
                            {att.name}
                          </strong>
                          <span className="attachment-meta">
                            {sizeKb} KB · {att.type || 'File'}
                          </span>
                        </div>
                        <div className="attachment-actions">
                          {att.data && (
                            <a
                              href={att.data}
                              download={att.name}
                              className="btn-icon btn-sm"
                              title="Download Attachment"
                            >
                              <Download size={14} />
                            </a>
                          )}
                          <button
                            type="button"
                            className="btn-icon btn-sm btn-icon-danger"
                            title="Remove Attachment"
                            onClick={() => handleRemoveAttachment(att.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Sharing Tab */}
          {activeTab === 'sharing' && (
            <div className="editor-sharing-tab">
              <div>
                <h3 className="section-title">Sharing & Granular Permissions</h3>
                <p className="section-subtitle">
                  Authorize collaborators with View or Edit access. Plaintext encryption keys are re-wrapped using cryptographic share grants.
                </p>
              </div>

              <div className="sharing-invite-form">
                <input
                  type="email"
                  className="input-field"
                  placeholder="Collaborator user email..."
                  value={shareRecipientEmail}
                  onChange={(e) => setShareRecipientEmail(e.target.value)}
                />
                <select
                  className="input-field select-field"
                  style={{ maxWidth: '140px' }}
                  value={sharePermission}
                  onChange={(e) => setSharePermission(e.target.value)}
                >
                  <option value="VIEW">View Only</option>
                  <option value="EDIT">View & Edit</option>
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddShare}
                  disabled={!shareRecipientEmail.trim()}
                >
                  <Plus size={15} />
                  <span>Add</span>
                </button>
              </div>

              <div className="shares-list">
                <h4 className="shares-list-title">Active Collaborators</h4>
                {sharesList.length === 0 ? (
                  <div className="shares-empty">
                    <Share2 size={32} opacity={0.3} />
                    <p>This secure note is private. Only you have decryption authorization.</p>
                  </div>
                ) : (
                  <div className="shares-table">
                    {sharesList.map((sh) => (
                      <div key={sh.id} className="share-row">
                        <div className="share-email">
                          <strong>{sh.email}</strong>
                        </div>
                        <div className="share-perm-badge">
                          <span className={`badge ${sh.permission === 'EDIT' ? 'badge-primary' : 'badge-secondary'}`}>
                            {sh.permission === 'EDIT' ? 'Can Edit' : 'Can View'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-icon btn-sm btn-icon-danger"
                          title="Revoke Access"
                          onClick={() => handleRemoveShare(sh.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="modal-footer secure-note-editor-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !hasEditPermission || !title.trim()}
            >
              <Save size={16} />
              <span>{saving ? 'Encrypting & Saving...' : 'Save Secure Note'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
